// File: client/src/variants/royale/useRoyaleRuntime.js
//
// Chess Royale runtime. Battle-royale chess: the outer ring of the board collapses
// every 5 full turns (8x8 -> 6x6 -> 4x4 -> 2x2). Pieces caught outside the playable
// area are destroyed; if a king is destroyed, that side loses. Otherwise standard
// checkmate applies.
//
// Everything runs through the variant contract — NO host or engine changes:
//   - afterMove: counts moves, applies the shrink by mutating the committed board,
//     and reports how many moves remain until the next shrink.
//   - transformLegalMoves: forbids moving onto already-destroyed tiles.
//   - getVictory: reports king elimination (last king alive wins).
//   - renderOverlay: a shrink timer + current arena size.

import React, { useRef, useState, useCallback } from 'react';
import { ShieldAlert, Shrink } from 'lucide-react';
import {
  SHRINK_EVERY, shrinkLevel, playableSize, isPlayable, applyShrink, kingElimination,
} from './shrink';

export function useRoyaleRuntime({ resumePayload }) {
  // moveCount: total half-moves made. fullTurns = floor(moveCount/2). We shrink when
  // crossing into a new shrink level. Persisted so a resumed game keeps its timeline.
  const moveCountRef = useRef(resumePayload ? resumePayload.moveCount || 0 : 0);
  const lastLevelRef = useRef(resumePayload ? resumePayload.lastLevel || 0 : 0);
  const [display, setDisplay] = useState({
    size: playableSize(Math.floor(moveCountRef.current / 2)),
    movesToShrink: SHRINK_EVERY * 2 - (moveCountRef.current % (SHRINK_EVERY * 2)),
    level: lastLevelRef.current,
  });

  const moveMiddleware = {
    // Forbid moving onto a tile that has already collapsed.
    transformLegalMoves(gameState, from, engineMoves) {
      const fullTurns = Math.floor(moveCountRef.current / 2);
      return engineMoves.filter(m => isPlayable(m.row, m.col, fullTurns));
    },

    afterMove(newState) {
      moveCountRef.current += 1;
      const fullTurns = Math.floor(moveCountRef.current / 2);
      const level = shrinkLevel(fullTurns);

      // Crossed into a new shrink level -> collapse the ring on the committed board.
      if (level > lastLevelRef.current) {
        lastLevelRef.current = level;
        applyShrink(newState.board, fullTurns);
      }

      const size = playableSize(fullTurns);
      const period = SHRINK_EVERY * 2; // half-moves between shrinks
      const movesToShrink = level >= 3 ? 0 : period - (moveCountRef.current % period);
      setDisplay({ size, movesToShrink, level });

      // Royale never keeps the turn open; standard turn flow.
      return { turnEnded: true };
    },
  };

  // Last king alive (or draw if both fell) overrides / precedes normal checkmate.
  const getVictory = useCallback((gameState, defaultStatus) => {
    const elim = kingElimination(gameState.board);
    if (elim) return elim;
    return defaultStatus && defaultStatus.over ? defaultStatus : null;
  }, []);

  const getPersistencePayload = useCallback(() => ({
    moveCount: moveCountRef.current,
    lastLevel: lastLevelRef.current,
  }), []);

  const renderOverlay = (slot) => {
    if (slot !== 'top') return null;
    const atMin = display.level >= 3;
    return (
      <div className="flex flex-wrap gap-2 items-center justify-center my-2">
        <span className="flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full border bg-orange-600/30 border-orange-500/50 text-orange-200">
          <Shrink size={14} /> Arena: {display.size}×{display.size}
        </span>
        <span className="flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full border bg-red-600/30 border-red-500/50 text-red-200">
          <ShieldAlert size={14} />
          {atMin ? 'Arena at minimum (2×2)' : `Ring collapses in ${display.movesToShrink} move${display.movesToShrink === 1 ? '' : 's'}`}
        </span>
      </div>
    );
  };

  return { moveMiddleware, getVictory, getPersistencePayload, renderOverlay };
}
